import { BlogPost, Tag } from '@chrisdunne-com/chrisdunne-types'
import React, { useEffect, useState } from 'react'
import { Col, Input, Label, Row } from 'reactstrap'
import { v4 } from 'uuid'
import { MultiSelect } from 'react-multi-select-component'
import { Editor } from '../../components/rte/Editor'
import { getData, putData } from '../../utils/server'
import { createImageAltText } from '../helper'
import { createSlugFromTitle } from '../../utils/resources'

type Option = {
  value: string
  label: string
}

export default function AddBlogModal(props: {
  setmodal_backdrop: React.Dispatch<React.SetStateAction<boolean>>
  put_blog_post: Function
  blogPost?: BlogPost
}) {
  const [editorValue, setEditorValue] = useState(props.blogPost?.content ?? '')
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)
  const [tags, setTags] = useState<Option[]>([])
  const [selectedTags, setSelectedTags] = useState<Option[]>([])

  useEffect(() => {
    if (!success && !error) {
      getData('/tag').then(
        (result: any) => {
          const theTags = result.map((tag: Tag) => ({
            label: tag.name,
            value: tag.name,
          }))
          setTags(theTags)
          setSelectedTags(
            props.blogPost?.tags.map((item) => ({
              label: item,
              value: item,
            })) ?? [],
          )
          setSuccess(true)
        },
        (error: any) => {
          setError(error)
        },
      )
    }
  })

  function handleBlogSubmit(event: any) {
    event.preventDefault()

    const data: BlogPost = {
      id: props.blogPost?.id ?? v4(),
      external: event.target.elements.external.checked,
      link: event.target.elements.external.checked
        ? {
            href: event.target.elements.linkHref.value,
            target: event.target.elements.external.checked ? '_blank' : '',
          }
        : undefined,
      mainImage: {
        src: event.target.elements.mainImageSrc.value,
        alt: createImageAltText(
          event.target.elements.title.value,
          'feature image',
        ),
      },
      author: {
        image: {
          src: 'https://media.chrisdunne.com/img/static/original/about.webp',
          alt: 'author image',
        },
        link: {
          href: event.target.elements.authorLinkHref.value,
          target: '_blank',
        },
        name: event.target.elements.authorName.value,
      },
      mins: event.target.elements.minutes.value,
      title: event.target.elements.title.value,
      description: event.target.elements.description.value,
      content: editorValue,
      tags: selectedTags?.map((item) => item.value) ?? [],
      lastUpdated: new Date(),
      publishDate: props.blogPost?.publishDate ?? new Date(),
      slug: createSlugFromTitle(event.target.elements.title.value),
      status: 'published',
    }

    putData('/post', data).then(
      () => {
        props.put_blog_post(data)
        props.setmodal_backdrop(false)
      },
      (error) => {
        console.log(error)
      },
    )
  }

  function updateLinkRefRequired(linkRequired: boolean) {
    (document.getElementById('linkHref')! as HTMLInputElement).required =
      linkRequired
  }

  function editor_updated(value: string) {
    setEditorValue(value)
  }

  function tags_changed(value: Option[]) {
    setSelectedTags(value)
  }

  return (
    <div className="m-4">
      <div className="modal-header">
        <h5 className="modal-title" id="staticBackdropLabel">
          Add Blog Post
        </h5>
        <button
          type="button"
          className="btn-close"
          onClick={() => {
            props.setmodal_backdrop(false)
          }}
          aria-label="Close"
        ></button>
      </div>
      <div className="modal-body">
        <form className="form" onSubmit={handleBlogSubmit}>
          <Row className="mb-4">
            <Label htmlFor="linkHref" className="col-sm-3 col-form-label">
              External Resource:
            </Label>
            <Col sm={9}>
              <Input
                type="checkbox"
                id="external"
                name="external"
                defaultChecked={props.blogPost?.external}
                onChange={(e) => updateLinkRefRequired(e.target.checked)}
                style={{ marginTop: '10px', height: '18px', width: '18px' }}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Label htmlFor="linkHref" className="col-sm-3 col-form-label">
              Link to Blog:
            </Label>
            <Col sm={9}>
              <Input
                type="text"
                className="form-control"
                id="linkHref"
                name="linkHref"
                defaultValue={props.blogPost?.link?.href}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Label htmlFor="mainImageSrc" className="col-sm-3 col-form-label">
              Main Image Url:
            </Label>
            <Col sm={9}>
              <Input
                type="text"
                className="form-control"
                id="mainImageSrc"
                name="mainImageSrc"
                required
                defaultValue={props.blogPost?.mainImage.src}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Label htmlFor="authorLinkHref" className="col-sm-3 col-form-label">
              Author Link Url:
            </Label>
            <Col sm={9}>
              <Input
                type="text"
                className="form-control"
                id="authorLinkHref"
                name="authorLinkHref"
                required
                defaultValue={
                  props.blogPost?.author.link.href ??
                  'https://www.linkedin.com/in/chris-dunne/'
                }
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Label htmlFor="authorName" className="col-sm-3 col-form-label">
              Author Name:
            </Label>
            <Col sm={9}>
              <Input
                type="text"
                className="form-control"
                id="authorName"
                name="authorName"
                defaultValue={props.blogPost?.author.name ?? 'Chris Dunne'}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Label htmlFor="minutes" className="col-sm-3 col-form-label">
              Minutes:
            </Label>
            <Col sm={9}>
              <Input
                type="number"
                className="form-control"
                id="minutes"
                name="minutes"
                required
                defaultValue={props.blogPost?.mins}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Label htmlFor="title" className="col-sm-3 col-form-label">
              Title:
            </Label>
            <Col sm={9}>
              <Input
                type="text"
                className="form-control"
                id="title"
                name="title"
                required
                defaultValue={props.blogPost?.title}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Label htmlFor="description" className="col-sm-3 col-form-label">
              Description:
            </Label>
            <Col sm={9}>
              <Input
                type="textarea"
                className="form-control"
                id="description"
                name="description"
                maxLength={500}
                rows={5}
                required
                defaultValue={props.blogPost?.description}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Label htmlFor="content" className="col-sm-3 col-form-label">
              Content:
            </Label>
            <Col sm={9}>
              <Editor
                onChange={editor_updated}
                defaultValue={props.blogPost?.content ?? ''}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Label htmlFor="tag" className="col-sm-3 col-form-label">
              Tags:
            </Label>
            <Col sm={9}>
              <MultiSelect
                options={tags}
                value={selectedTags}
                onChange={tags_changed}
                labelledBy="tags"
              />
            </Col>
          </Row>
          <div className="modal-footer">
            <input type="submit" value="Submit" className="btn btn-primary" />
          </div>
        </form>
      </div>
    </div>
  )
}
